import React, { ReactElement, useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Button, ClickAwayListener, Tooltip, Typography } from '@mui/material';
import { UnknownAction } from 'redux';
import { makeStyles } from 'tss-react/mui';

import { mainApi, mapDataToUndefinedIfSkip } from 'modules/api';
import { getWalletIconById, useConnection } from 'modules/auth';
import { useGetAccountsQuery } from 'modules/auth/actions/getAccounts';
import { getCurrentAccount } from 'modules/auth/actions/getCurrentAccount';
import { useSwitchAccountMutation } from 'modules/auth/actions/switchAccount';
import { authCacheTags } from 'modules/auth/authCacheTags';
import { Card, cropString } from 'modules/common';
import { KnownDialogs, useDialog } from 'modules/dialogs';
import { globalTranslation, useTranslation } from 'modules/i18n';
import { ArrowDownIcon, CheckedIcon } from 'modules/icons';

export function Account(): ReactElement {
  const { keys, t } = useTranslation(globalTranslation);
  const { classes, cx } = useStyles();
  const { disconnect, data, connectionKey, isConnected } = useConnection();
  const dispatch = useDispatch();

  const { data: accounts } = useGetAccountsQuery(
    { connectionKey },
    { skip: !isConnected, selectFromResult: mapDataToUndefinedIfSkip },
  );
  const [switchAccount] = useSwitchAccountMutation();

  const [open, setOpen] = useState(false);

  const { onOpen } = useDialog(KnownDialogs.connect);

  const handleDisconnect = useCallback(async () => {
    await disconnect();
    setOpen(false);
  }, [disconnect]);

  const handleSwitchAccount = useCallback(
    async (account: string) => {
      await switchAccount({ account });
      mainApi.util.invalidateTags([authCacheTags.account]);

      dispatch(
        getCurrentAccount.initiate(undefined) as unknown as UnknownAction,
      );
      setOpen(false);
    },
    [dispatch, switchAccount],
  );

  if (!data?.address) {
    return (
      <Button color="secondary" onClick={onOpen}>
        {t(keys.common.connectWallet)}
      </Button>
    );
  }

  const WalletIcon = data?.walletId && getWalletIconById(data?.walletId);

  return (
    <ClickAwayListener onClickAway={() => setOpen(false)}>
      <div>
        <Tooltip
          disableFocusListener
          disableHoverListener
          disableTouchListener
          arrow={false}
          classes={{ tooltip: classes.tooltip }}
          open={open}
          PopperProps={{ disablePortal: true }}
          title={
            <Card secondaryColor wrapClassName={classes.tooltipContent}>
              {!!accounts?.length && accounts.length > 1 && (
                <div className={classes.menu}>
                  {accounts?.map(account => {
                    const isCurrent = account === data?.address;

                    return (
                      <Typography
                        key={account}
                        className={cx(
                          classes.menuItem,
                          isCurrent && classes.disabled,
                        )}
                        onClick={() => handleSwitchAccount(account)}
                      >
                        <span>{cropString(account)}</span>

                        {isCurrent && <CheckedIcon />}
                      </Typography>
                    );
                  })}
                </div>
              )}

              <div className={classes.menu} onClick={handleDisconnect}>
                <Typography className={classes.menuItem}>
                  {t(keys.common.disconnect)}
                </Typography>
              </div>
            </Card>
          }
          onClose={() => setOpen(false)}
        >
          <div className={classes.action} onClick={() => setOpen(!open)}>
            {WalletIcon && <WalletIcon className={classes.walletIcon} />}

            {cropString(data?.address)}

            <ArrowDownIcon
              className={cx(classes.icon, open && classes.openIcon)}
            />
          </div>
        </Tooltip>
      </div>
    </ClickAwayListener>
  );
}

export const useStyles = makeStyles()(theme => ({
  tooltip: {
    background: 'transparent',
  },
  action: {
    padding: theme.spacing(1, 4),
    height: 36,
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    cursor: 'pointer',
    fontSize: 14,
    border: `2px solid ${theme.palette.background.default}`,
    borderRadius: 8,
    backgroundColor: theme.palette.primary.contrastText,

    [theme.breakpoints.down('sm')]: {
      gap: theme.spacing(1),
      padding: theme.spacing(1, 2),
    },
  },

  tooltipContent: {
    padding: 0,
    borderRadius: 8,
    minWidth: 174,
  },

  menu: {
    padding: theme.spacing(1),

    '&:not(:last-child)': {
      borderBottom: `2px solid ${theme.palette.background.default}`,
    },
  },

  menuItem: {
    fontSize: 14,
    fontWeight: 500,
    padding: theme.spacing(2, 4),
    width: '100%',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    cursor: 'pointer',
    borderRadius: 8,

    '&:hover': {
      backgroundColor: theme.palette.secondary.dark,
    },
  },

  disabled: {
    pointerEvents: 'none',
  },

  disconnect: {
    fontSize: 14,
    marginTop: theme.spacing(-2),

    [theme.breakpoints.down('sm')]: {
      marginTop: theme.spacing(-4),
    },
  },

  walletIcon: {
    width: 20,
  },
  icon: {
    transition: 'all 0.2s ease-in-out',
  },
  openIcon: {
    transform: 'rotate(180deg)',
  },
}));
